import { API_ORIGIN, authorizedFetch, parseApiErrorMessage } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type ProfileVisibility = 'employers' | 'public'

export type PrivacySettings = {
  profile_visibility: ProfileVisibility
  public_slug: string | null
  notice_acknowledged: boolean
  can_close_account: boolean
  close_blocked_reason: string | null
  requires_password: boolean
  purge_grace_days: number
}

export const PRIVACY_SETTINGS_QUERY_KEY = ['talentco-privacy-settings'] as const

const SETTINGS_URL = `${API_ORIGIN}/api/privacy/settings`

export function usePrivacySettings(enabled = true) {
  return useQuery({
    queryKey: PRIVACY_SETTINGS_QUERY_KEY,
    enabled,
    queryFn: async () => {
      const response = await authorizedFetch(SETTINGS_URL)
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not load privacy settings'))
      }
      return (await response.json()) as PrivacySettings
    },
  })
}

export function useUpdatePrivacySettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: { profile_visibility?: ProfileVisibility; acknowledge_notice?: boolean }) => {
      const response = await authorizedFetch(SETTINGS_URL, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not save privacy settings'))
      }
      return (await response.json()) as PrivacySettings
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PRIVACY_SETTINGS_QUERY_KEY, data)
    },
  })
}

export function useCloseAccount() {
  return useMutation({
    mutationFn: async (payload: { confirm: string; password?: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/privacy/close-account`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not close your account'))
      }
      return true
    },
  })
}

export function publicProfileUrl(slug: string): string {
  return `${window.location.origin}/p/${encodeURIComponent(slug)}`
}
